import { GripHorizontal, GripVertical, Trash2 } from "lucide-react";
import type { ComponentType, SVGProps } from "react";
import { useEffect, useRef, useState } from "react";

/** Where the palette is docked relative to its container. */
export type PaletteAnchor = "left" | "right" | "top" | "bottom";

/** Visual grouping affects only the divider lines between adjacent tools. */
export type ToolGroup = "select" | "place" | "draw" | "insert" | "custom";

export interface ToolDef<TId extends string = string> {
  id: TId;
  label: string;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  group: ToolGroup;
}

export interface ToolStyleState {
  color: string;
  width: number;
  dashed: boolean;
}

interface ToolPaletteProps<TId extends string> {
  /** Full list of selectable tools, rendered in order with group dividers. */
  tools: readonly ToolDef<TId>[];
  mode: TId;
  onMode: (mode: TId) => void;
  hasSelection: boolean;
  onDeleteSelection: () => void;
  style: ToolStyleState;
  onStyle: (patch: Partial<ToolStyleState>) => void;
  /** Current dock side. */
  anchor: PaletteAnchor;
  /** Called when the user finishes a drag onto a new dock side. */
  onAnchor: (next: PaletteAnchor) => void;
  /** Tool ids that should show the colour / width / dashed style card. */
  drawToolIds: ReadonlySet<TId>;
  /** Colour swatches in the style card. Defaults to the canvas-kit palette. */
  colors?: readonly string[];
}

const DEFAULT_COLORS = [
  "#97fc00",
  "#ffffff",
  "#ffb02e",
  "#ff798f",
  "#78aaff",
  "#bd9cff",
  "#101418",
] as const;

/**
 * Floating tool palette docked to one of four edges of the stage. When
 * docked left/right it lays out vertically; when docked top/bottom it
 * lays out horizontally and overflows with horizontal scroll.
 *
 * The grip handle (rendered at the bottom on vertical anchors / at the
 * trailing edge on horizontal anchors) starts a pointer-drag. While
 * dragging, four translucent drop-zones light up along the container
 * edges; releasing inside one snaps the palette to that anchor.
 */
export function ToolPalette<TId extends string>({
  tools,
  mode,
  onMode,
  hasSelection,
  onDeleteSelection,
  style,
  onStyle,
  anchor,
  onAnchor,
  drawToolIds,
  colors = DEFAULT_COLORS,
}: ToolPaletteProps<TId>) {
  const showStyle = drawToolIds.has(mode);
  const isVertical = anchor === "left" || anchor === "right";
  const [dragging, setDragging] = useState(false);
  const [hoverAnchor, setHoverAnchor] = useState<PaletteAnchor | null>(null);
  // The palette is always positioned relative to its nearest positioned
  // ancestor. We capture that DOM rect at drag-start so anchor detection
  // works no matter where the editor sits on the page.
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Outer wrapper layout & docking. We use absolute positioning along
  // the chosen edge; for top/bottom we stretch full-width and enable
  // horizontal scrolling on the inner card.
  const outerPosClass: Record<PaletteAnchor, string> = {
    left: "left-3 top-3 bottom-3 flex-row items-start",
    right: "right-3 top-3 bottom-3 flex-row-reverse items-start",
    top: "left-3 right-3 top-3 flex-col items-start",
    bottom: "left-3 right-3 bottom-3 flex-col-reverse items-start",
  };
  // Inner tools card direction.
  const toolsFlex = isVertical ? "flex-col" : "flex-row";
  // Style panel direction & sizing tweaks per anchor.
  // Horizontal: size to content (drop `w-full`) so the card doesn't stretch
  // across the full stage width when the toolbar is anchored top/bottom.
  const styleCardClass = isVertical ? "flex-col" : "flex-row flex-wrap";

  function pickAnchor(x: number, y: number, rect: DOMRect): PaletteAnchor {
    // Visual drop-zones: top/bottom bands span the full width and take
    // priority. Left/right zones only exist in the middle strip between
    // those two bands so the corners are unambiguous (matches the
    // dashed-outline overlay the user sees while dragging).
    const BAND = 60;
    if (y < rect.top + BAND) return "top";
    if (y > rect.bottom - BAND) return "bottom";
    return x - rect.left < rect.right - x ? "left" : "right";
  }

  // Drag lifecycle — bound only while `dragging` is true so we don't
  // leak global listeners when the palette is idle.
  useEffect(() => {
    if (!dragging) return;
    const el = rootRef.current?.parentElement;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const handleMove = (e: PointerEvent) => {
      setHoverAnchor(pickAnchor(e.clientX, e.clientY, rect));
    };
    const handleUp = (e: PointerEvent) => {
      const next = pickAnchor(e.clientX, e.clientY, rect);
      setDragging(false);
      setHoverAnchor(null);
      if (next !== anchor) onAnchor(next);
    };
    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    window.addEventListener("pointercancel", handleUp);
    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
      window.removeEventListener("pointercancel", handleUp);
    };
  }, [dragging, anchor, onAnchor]);

  const GripIcon = isVertical ? GripHorizontal : GripVertical;

  // Drag handle button. Vertical-anchored palette: full-width, short
  // (h-10) bar at the bottom. Horizontal-anchored palette: full-height
  // (h-14, matching the tool buttons), narrow (w-10) bar at the leading
  // (left) edge. PointerDown stops propagation so the underlying Konva
  // stage doesn't open a context menu / treat the press as a tap-action.
  const handleBtn = (
    <button
      type="button"
      title="Palette verschieben"
      aria-label="Palette verschieben (oben, unten, links, rechts)"
      onPointerDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
        (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
        setDragging(true);
      }}
      onClick={(e) => {
        // Block any synthetic click that might bubble to a parent
        // handler (e.g. selection-clear on the stage).
        e.stopPropagation();
      }}
      onContextMenu={(e) => e.preventDefault()}
      className={`flex shrink-0 cursor-grab items-center justify-center rounded-2xl bg-white/4 text-muted-foreground transition-colors hover:bg-white/10 hover:text-white active:cursor-grabbing ${
        isVertical ? "h-10 w-14" : "h-14 w-10"
      }`}
      style={{ touchAction: "none" }}
    >
      <GripIcon className="size-4" aria-hidden />
    </button>
  );
  const handleDivider = isVertical ? (
    <div className="my-1 h-px w-full bg-white/10" />
  ) : (
    <div className="mx-1 h-8 w-px self-center bg-white/10" />
  );

  return (
    <>
      {/* Drop-zone hints while dragging — semi-transparent green bars on
          each edge of the parent container. The active edge highlights. */}
      {dragging && (
        <div className="pointer-events-none absolute inset-0 z-30">
          {(["left", "right", "top", "bottom"] as const).map((a) => {
            const active = hoverAnchor === a;
            const base = "absolute transition-opacity rounded-2xl border-2 border-dashed";
            const pos =
              a === "left"
                ? "left-1 top-14 bottom-14 w-12"
                : a === "right"
                  ? "right-1 top-14 bottom-14 w-12"
                  : a === "top"
                    ? "left-1 right-1 top-1 h-12"
                    : "left-1 right-1 bottom-1 h-12";
            return (
              <div
                key={a}
                className={`${base} ${pos}`}
                style={{
                  borderColor: active ? "rgba(151,252,0,0.9)" : "rgba(151,252,0,0.35)",
                  background: active ? "rgba(151,252,0,0.18)" : "rgba(151,252,0,0.06)",
                  opacity: active ? 1 : 0.85,
                }}
              />
            );
          })}
        </div>
      )}

      <div
        ref={rootRef}
        className={`pointer-events-none absolute z-20 flex gap-2 ${outerPosClass[anchor]}`}
      >
        <div
          className={`glass-floating pointer-events-auto flex ${toolsFlex} gap-1 rounded-3xl p-1.5 ${
            isVertical
              ? "max-h-full overflow-y-auto overflow-x-hidden"
              : "max-w-full overflow-x-auto overflow-y-hidden"
          }`}
        >
          {/* Horizontal anchor: handle goes FIRST (left edge of the bar). */}
          {!isVertical && (
            <>
              {handleBtn}
              {handleDivider}
            </>
          )}
          {tools.map(({ id, label, Icon, group }, i) => {
            const active = mode === id;
            const prevGroup = i > 0 ? tools[i - 1]!.group : null;
            const showDivider = prevGroup && prevGroup !== group;
            return (
              <div key={id} className={isVertical ? "" : "flex items-center gap-1"}>
                {showDivider &&
                  (isVertical ? (
                    <div className="my-1 h-px w-full bg-white/10" />
                  ) : (
                    <div className="mx-1 h-8 w-px self-center bg-white/10" />
                  ))}
                <button
                  type="button"
                  onClick={() => onMode(id)}
                  title={label}
                  aria-label={label}
                  aria-pressed={active}
                  className={`group flex h-14 w-14 shrink-0 flex-col items-center justify-center gap-0.5 overflow-hidden rounded-2xl px-1 text-[9px] font-semibold uppercase tracking-wider transition-colors ${
                    active
                      ? "bg-pitch-lime text-[#1d3700] shadow-glow"
                      : "bg-white/4 text-muted-foreground hover:bg-white/10 hover:text-white"
                  }`}
                >
                  <Icon className="size-4" />
                  <span className="max-w-full truncate leading-none">{label}</span>
                </button>
              </div>
            );
          })}

          {isVertical ? (
            <div className="my-1 h-px w-full bg-white/10" />
          ) : (
            <div className="mx-1 h-8 w-px self-center bg-white/10" />
          )}

          <button
            type="button"
            onClick={onDeleteSelection}
            disabled={!hasSelection}
            title="Auswahl löschen"
            aria-label="Auswahl löschen"
            className="flex h-14 w-14 shrink-0 flex-col items-center justify-center gap-0.5 overflow-hidden rounded-2xl bg-[#3a1418]/70 px-1 text-[9px] font-semibold uppercase tracking-wider text-[#ffb1b8] transition-colors hover:bg-[#4a1c22] disabled:opacity-40"
          >
            <Trash2 className="size-4" />
            <span className="max-w-full truncate leading-none">Lösch.</span>
          </button>

          {/* Vertical anchor: handle goes LAST (bottom edge of the column). */}
          {isVertical && (
            <>
              {handleDivider}
              {handleBtn}
            </>
          )}
        </div>

        {showStyle && (
          <div
            className={`glass-floating pointer-events-auto flex gap-2 rounded-3xl p-2 ${styleCardClass}`}
          >
            <div
              className={
                isVertical ? "flex flex-wrap justify-center gap-1.5" : "flex items-center gap-1"
              }
            >
              {colors.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => onStyle({ color: c })}
                  className="h-6 w-6 shrink-0 rounded-full border-2 transition-transform hover:scale-110"
                  style={{
                    background: c,
                    borderColor: style.color === c ? "#97fc00" : "rgba(255,255,255,0.2)",
                  }}
                  aria-label={`Farbe ${c}`}
                />
              ))}
            </div>
            <div className="flex items-center gap-2 px-1">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">B</span>
              <input
                type="range"
                min={2}
                max={24}
                value={style.width}
                onChange={(e) => onStyle({ width: Number(e.target.value) })}
                className={isVertical ? "flex-1 accent-pitch-lime" : "w-24 accent-pitch-lime"}
              />
              <span className="w-5 text-right text-[11px] font-semibold">{style.width}</span>
            </div>
            <label className="flex items-center gap-2 px-1 text-[11px] text-muted-foreground">
              <input
                type="checkbox"
                checked={style.dashed}
                onChange={(e) => onStyle({ dashed: e.target.checked })}
                className="accent-pitch-lime"
              />
              Gestrichelt
            </label>
          </div>
        )}
      </div>
    </>
  );
}
